// SPDX-License-Identifier: AGPL-3.0-or-later
// In-app notifications, worked out in the browser from the decrypted data.
// Nothing here reaches the server unless phone notifications are on, and
// then only as the wording the user allowed.
import { addDays, periodRange, type Cents, type IsoDate } from "@almanac/domain";
import { commitments, MATCH_DAYS, scheduledByCategory, stillExpected } from "./commitments.js";
import { agenda } from "./events.js";
import { actualsThisMonth, rolledUp } from "./figures.js";
import { NOT_SPENDING } from "./categories.js";
import type { BudgetPlan, CalendarEvent, NotifyPrefs, Purchase, Transaction } from "./model.js";
import { activeSaves, PURCHASES_LINE, savingsPlans } from "./purchases.js";

/** How far ahead bills and events show. */
export const AHEAD_DAYS = 7;
/** A budget this full (actual plus scheduled) counts as nearly spent. */
export const NEAR_SHARE = 0.9;

export type NoticeType = keyof NotifyPrefs["types"];

export type Notice =
  | { id: string; type: "bills"; kind: "bill-due" | "bill-overdue" | "event-soon"; date: IsoDate; event: CalendarEvent }
  | { id: string; type: "budgets"; kind: "budget-over" | "budget-near"; date: IsoDate; category: string; budget: Cents; used: Cents }
  | { id: string; type: "goals"; kind: "goal-behind" | "goal-ready"; date: IsoDate; purchase: Purchase; projected: IsoDate | null };

interface Data {
  events: readonly CalendarEvent[];
  transactions: readonly Transaction[];
  plan: BudgetPlan;
  purchases?: readonly Purchase[];
}

/** Every current notification, before the user's choices and dismissals are applied. */
export function allNotices({ events, transactions, plan, purchases = [] }: Data, today: IsoDate): Notice[] {
  const notices: Notice[] = [];
  const end = addDays(today, AHEAD_DAYS);

  // Bills: costs not yet paid, from a few days overdue to a week ahead. Events
  // without a cost (a birthday, a renewal date) show only once they are close.
  const paid = new Set(
    commitments(events, transactions, addDays(today, -MATCH_DAYS), end)
      .filter((c) => c.paidBy)
      .map((c) => `${c.event.id}|${c.date}`),
  );
  for (const o of agenda(events, addDays(today, -MATCH_DAYS), end)) {
    const id = `${o.event.id}|${o.date}`;
    if ((o.event.amount ?? 0) > 0) {
      if (paid.has(id)) continue;
      notices.push({ id: `bill|${id}`, type: "bills", kind: o.date < today ? "bill-overdue" : "bill-due", date: o.date, event: o.event });
    } else if (o.date >= today && o.date <= addDays(today, 1)) {
      notices.push({ id: `event|${id}`, type: "bills", kind: "event-soon", date: o.date, event: o.event });
    }
  }

  // Budgets: a main category whose spending plus still-scheduled costs has
  // passed its budget this month, or is close.
  const month = periodRange("month", today);
  const actuals = actualsThisMonth(transactions, today);
  const scheduled = scheduledByCategory(stillExpected(commitments(events, transactions, month.start, month.end), today));
  for (const line of plan.lines) {
    if (NOT_SPENDING.has(line.id) || line.amount <= 0) continue;
    const used = rolledUp(actuals, line.id) + rolledUp(scheduled, line.id);
    const kind = used > line.amount ? "budget-over" : used >= line.amount * NEAR_SHARE ? "budget-near" : null;
    if (!kind) continue;
    // The id carries the month and level, so a new month or getting worse shows again after a dismissal.
    notices.push({
      id: `budget|${line.id}|${month.start.slice(0, 7)}|${kind === "budget-over" ? "over" : "near"}`,
      type: "budgets",
      kind,
      date: today,
      category: line.id,
      budget: line.amount,
      used,
    });
  }

  // Goals: a save-up purchase that will miss its date, or one fully saved for.
  const reserved = plan.lines.find((l) => l.id === PURCHASES_LINE)?.amount ?? 0;
  const plans = savingsPlans(purchases, reserved, plan.purchaseLimit ?? null, today);
  for (const purchase of activeSaves(purchases)) {
    const p = plans.get(purchase.id)!;
    if (p.pace === "complete") {
      notices.push({ id: `goal|${purchase.id}|ready`, type: "goals", kind: "goal-ready", date: today, purchase, projected: p.projected });
    } else if (p.pace === "behind" || p.pace === "overdue") {
      // Shows again each month while it stays behind.
      notices.push({ id: `goal|${purchase.id}|behind|${today.slice(0, 7)}`, type: "goals", kind: "goal-behind", date: today, purchase, projected: p.projected });
    }
  }
  return notices.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** The notifications to show: kinds the user wants, minus the ones dismissed. */
export function visibleNotices(all: readonly Notice[], prefs: NotifyPrefs): Notice[] {
  const dismissed = new Set(prefs.dismissed);
  return all.filter((n) => prefs.types[n.type] && !dismissed.has(n.id));
}
